import { commandFeedback } from '../features/notifications/events'
import {
  createAppRuntime,
  createHTTPTransport,
  type AppRuntime,
} from '@ovasabi/runtime-transport'
import { createAppRouteRegistry } from '../generated/runtimeRoutes'
import { prepareDocumentPhoto, prepareImage, type ImageAdvisory } from './imageProcessing'
import { getActiveRole } from './actor'
import { getEdition } from './edition'
import { getActivePrototypeRuntime } from '../stores/activeRuntime'

/**
 * Foundation runtime client — the seam between the ChooseChow UI and the
 * event-sourced backend. Two collaborators do all the work; this module is the
 * thin app-level facade that wires them and exposes a typed `commands.*`
 * catalog.
 *
 *   Writes are EVENTS. Every backend route is `MakeEventRoute(method, path,
 *     eventType, …)` (`httpapi/catalog.go`). The browser uses
 *     `createAppRuntime({registry, strategies})` from
 *     `@ovasabi/runtime-transport`: the registry comes from the generated
 *     `runtimeRoutes.ts` (sourced from the backend's own route catalog, so it
 *     can never drift), and the HTTP transport substitutes `{param}` segments
 *     and strips those keys from the body so the same `commands.*` shape works
 *     for every method.
 *
 *   Reads are PROJECTIONS. Committed state arrives via the Hermes hotplane and
 *     lands in the generated `prototypeStoreRegistry` stores read by the
 *     `use*Snapshot()` hooks. The UI never merges a command's HTTP response
 *     into local state; it dispatches the intent and lets the projection
 *     update the store (`docs/foundation/hermes_hotplane.md`).
 *
 * So `commands.*` is a thin, typed catalog naming the eventTypes the
 * ChooseChow surfaces emit. Path params and body fields ride in the same
 * payload object — the transport (see `resolvePathParams` in
 * runtime-transport/http.ts) routes scalars to the URL and the remainder to
 * the body. Edition + actor role ride along as headers via `getHeaders`, so
 * scope travels with every command without per-call plumbing.
 */

/* ── runtime mode ────────────────────────────────────────────────────── */

/** True once live projections are bound; offline mode still uses dummy stores. */
export const isLiveRuntime = (): boolean => getActivePrototypeRuntime().mode === 'live'

/**
 * Active read lane: `'live'` when the Hermes projection gateway is wired,
 * `'dummy'` when the UI is rendering seeded prototype data. Informative
 * sections render from seed in either mode; this lets the UI label *which*
 * data the user is looking at and prove the live path works once the gateway
 * is reachable.
 */
export const runtimeMode = (): string => getActivePrototypeRuntime().mode

/**
 * Whether the *client-side* offline-prototype catalog (`content.ts`) may be
 * rendered as a fallback. This is distinct from the database demo seed
 * (migration 000002, org_default): that is real backend data served over live
 * projections and is always available to a signed-in session. The bundled
 * `content.ts` arrays are the "offline prototype fields" — a dev affordance so
 * layouts render before a backend exists.
 *
 * A production build (`vite build`, i.e. every Coolify deploy) is live-only:
 * surfaces render backend projection data or an explicit empty state, never
 * fabricated catalog rows. `vite dev` / `make dev-local` keeps the affordance.
 * Set `VITE_ALLOW_DEMO_CONTENT=true` to force the affordance on in a build
 * (e.g. a design/staging preview).
 *
 * The companion rule at every read site: a signed-in session (isLiveRuntime())
 * shows backend data or empty regardless of this flag — so signing in with a
 * seeded profile shows exactly what the database holds, which is the live test
 * path. The curated fallback only applies pre-authentication, and only in dev.
 */
export const allowSeedContent = (): boolean =>
  !isLiveRuntime() && (!import.meta.env.PROD || import.meta.env.VITE_ALLOW_DEMO_CONTENT === 'true')

/* ── app runtime (registry + transport) ──────────────────────────────── */

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'

/** Resolve stored media through the API base. Keep bundled and external assets on their original origins. */
export type ImageWidth = 128 | 384 | 768

export const mediaUrl = (path?: string, width?: ImageWidth): string | undefined => {
  const raw = String(path ?? '').trim()
  if (!raw) return undefined
  if (width) {
    const original = mediaUrl(raw)
    if (!original) return undefined
    const localSeed = original.match(/^\/seed\/(chef|dish|menu|vendor)\/([^/?]+)\.webp$/)
    if (localSeed) return `/seed/thumbs/${localSeed[1]}/${localSeed[2]}-${width}.webp`
    try {
      const url = new URL(original, 'https://media.invalid')
      const api = new URL(API_BASE_URL, 'https://media.invalid')
      const prefix = api.pathname.replace(/\/$/, '')
      if (url.origin === api.origin && (url.pathname.startsWith(`${prefix}/v1/media/objects/`) || url.pathname.startsWith('/v1/media/objects/'))) {
        url.searchParams.set('width', String(width))
        return original.startsWith('/') ? `${url.pathname}${url.search}${url.hash}` : url.toString()
      }
    } catch { /* Preserve sources that cannot use server transformations. */ }
    return original
  }
  if (/^(https?:|blob:|data:)/i.test(raw)) return raw
  if (raw.startsWith('/v1/media/objects/seed/')) {
    return raw.replace('/v1/media/objects/seed/', '/seed/').replace(/\.(png|jpe?g)$/i, '.webp')
  }
  if (raw.startsWith('/seed/')) {
    return raw.replace(/\.(png|jpe?g)$/i, '.webp')
  }
  if (!raw.startsWith('/')) return raw
  if (!raw.startsWith('/v1/media/objects/')) return raw
  return `${API_BASE_URL.replace(/\/$/, '')}${raw}`
}

/**
 * Headers every command carries. Auth + edition + role travel here, replacing
 * the per-request injection that used to live in `lib/api.ts`. The HTTP
 * transport calls this on every dispatch, so token rotations / role switches
 * picked up by `actor.ts` and `edition.ts` are observed without restarting the
 * bus.
 */
const transportHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {
    'X-Chow-Edition': getEdition(),
    'X-Chow-Role': getActiveRole(),
  }
  if (typeof localStorage !== 'undefined') {
    const token = localStorage.getItem('auth_token')
    if (token) headers.Authorization = `Bearer ${token}`
  }
  return headers
}

const runtime: AppRuntime = createAppRuntime({
  registry: createAppRouteRegistry(),
  strategies: [
    createHTTPTransport({
      baseUrl: typeof window !== 'undefined' ? new URL(API_BASE_URL, window.location.origin).toString() : API_BASE_URL,
      getHeaders: transportHeaders,
    }),
  ],
})

export const dispatch = async (...args: Parameters<typeof runtime.dispatch>) => {
  try {
    const result = await runtime.dispatch(...args)
    commandFeedback(String(args[0]), (args[1] ?? {}) as Record<string, unknown>, commandPayload(result), String(result.correlationId ?? ''))
    return result
  } catch (error: unknown) {
    const err = error as { status?: number; message?: string } | null | undefined
    if (err?.status === 401 || (err?.message && err.message.includes('401'))) {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('chow:auth:unauthorized'))
      }
    }
    throw error
  }
}
export const canDispatch = runtime.canDispatch

/** Read handler data from the Foundation dispatch result or a direct transport response. */
export const commandPayload = <T extends Record<string, unknown> = Record<string, unknown>>(
  result: unknown,
): T => {
  if (!result || typeof result !== 'object' || Array.isArray(result)) return {} as T
  const record = result as Record<string, unknown>
  const wrapped = typeof record.correlationId === 'string' && typeof record.requestId === 'string' && typeof record.idempotencyKey === 'string'
  const payload = wrapped ? record['data'] : record
  return (payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {}) as T
}

/* ── command catalog (named eventTypes the UI emits) ─────────────────── */

/**
 * Each entry names a foundation eventType; path params and body fields are
 * passed together — the transport substitutes scalars into `{name}` segments
 * and strips them from the body so they land in exactly one place. Adding a
 * domain action is a one-line catalog entry, not a new bespoke method — that
 * is the "generic" property the foundation asks for.
 */
export const commands = {
  notifications: {
    settings: () => dispatch('notification:get_settings:v1:requested', {}),
    subscribe: (payload: Record<string, unknown>) => dispatch('notification:subscribe:v1:requested', payload),
    unsubscribe: (endpoint: string) => dispatch('notification:unsubscribe:v1:requested', { endpoint }),
    test: (endpoint: string) => dispatch('notification:test:v1:requested', { endpoint }),
  },
  promotions: {
    list: (offset = 0) => dispatch('marketplace:list_promos:v1:requested', { offset }),
    create: (payload: Record<string, unknown>) => dispatch('marketplace:create_promo:v1:requested', payload),
    setStatus: (campaignId: string, active: boolean) => dispatch('marketplace:set_promo_status:v1:requested', { campaign_id: campaignId, is_active: active }),
    archive: (campaignId: string) => dispatch('marketplace:archive_promo:v1:requested', { campaign_id: campaignId }),
    quote: (payload: Record<string, unknown>) => dispatch('marketplace:quote_promo:v1:requested', payload),
  },
  mealplan: {
    getRates: () => dispatch('mealplan:get_rates:v1:requested', {}),
    setRates: (payload: Record<string, unknown>) => dispatch('mealplan:set_rates:v1:requested', payload),
  },
  marketplace: {
    /**
     * Price the whole cart kitchen by kitchen, as checkout will bill it.
     * The server owns this arithmetic; the cart displays what it returns.
     */
    quoteCart: (payload: Record<string, unknown>) =>
      dispatch('marketplace:quote_cart:v1:requested', payload),
    addCartItem: (payload: Record<string, unknown>) =>
      dispatch('marketplace:add_cart_item:v1:requested', payload),
    checkoutCart: (payload: Record<string, unknown>) =>
      dispatch('marketplace:checkout_cart:v1:requested', payload),
    /** Hold a single dish on a future date without paying or touching the cart. */
    createReservation: (payload: Record<string, unknown>) =>
      dispatch('marketplace:create_reservation:v1:requested', payload),
    getOrder: (orderId: string) =>
      dispatch('marketplace:get_order:v1:requested', { order_id: orderId }),
    /**
     * Read the active delivery and service fees. Readable by any signed-in
     * actor: these are the rates every customer is charged.
     */
    getPlatformFees: () =>
      dispatch('marketplace:get_platform_fees:v1:requested', {}),
    /**
     * Publish a new version of the delivery and service fees. Operator only.
     * Amounts are sent as decimal strings of minor units (kobo).
     */
    setPlatformFees: (payload: Record<string, unknown>) =>
      dispatch('marketplace:set_platform_fees:v1:requested', payload),
    setOrderStatus: (orderId: string, payload: Record<string, unknown>) =>
      dispatch('marketplace:set_order_status:v1:requested', {
        order_id: orderId,
        ...payload,
      }),
    /**
     * Operator-only vendor review (admin capability). `is_open` is optional and
     * omitted means "leave the kitchen's open/closed sign alone" — passing it
     * unnecessarily is how verifying a chef used to also close them.
     */
    adminSetVendorStatus: (vendorProfileId: string, payload: Record<string, unknown>) =>
      dispatch('marketplace:admin_set_vendor_status:v1:requested', {
        vendor_profile_id: vendorProfileId,
        ...payload,
      }),
  },
  billing: {
    /**
     * Money ChooseChow holds for chefs, and money chefs owe it. Operator only;
     * the route requires the admin permission.
     */
    listPayoutReceivables: (offset = 0, limit = 50) =>
      dispatch('billing:list_payout_receivables:v1:requested', { offset, limit }),
    /**
     * Pay one kitchen's held money (operator only). `expected_amount_minor` is
     * the held total the operator saw; a changed total is refused. `method` is
     * `paystack_transfer`, or `manual` with `external_reference` and `note`.
     */
    releaseChefPayout: (payload: Record<string, unknown>) =>
      dispatch('billing:release_chef_payout:v1:requested', payload),
    /** The signed-in chef's own earnings and payouts, from the payout ledger. */
    listMyPayouts: () => dispatch('billing:list_my_payouts:v1:requested', {}),
    /** Payouts of held chef money, newest first (operator only). */
    listPayoutReleases: (vendorProfileId = '', limit = 50) =>
      dispatch('billing:list_payout_releases:v1:requested', { vendor_profile_id: vendorProfileId, limit }),
    listPlans: () => dispatch('billing:list_subscription_plans:v1:requested', {}),
    /** Open a payment session for an order (or subscription); returns a reference. */
    createCheckoutSession: (payload: Record<string, unknown>) =>
      dispatch('billing:create_checkout_session:v1:requested', payload),
    /** Capture a payment by its session reference, promoting the order to `paid`. */
    confirmPayment: (payload: Record<string, unknown>) =>
      dispatch('billing:confirm_payment:v1:requested', payload),
    /** Operator-only (admin capability): refund a captured payment. */
    requestRefund: (payload: Record<string, unknown>) =>
      dispatch('billing:request_refund:v1:requested', payload),
    createSubscription: (payload: Record<string, unknown>) =>
      dispatch('billing:create_subscription:v1:requested', payload),
    cancelSubscription: (subscriptionId: string) =>
      dispatch('billing:cancel_subscription:v1:requested', {
        subscription_id: subscriptionId,
      }),
  },
  profile: {
    upsertProfile: (payload: Record<string, unknown>) =>
      dispatch('profile:upsert_profile:v1:requested', payload),
    getProfile: (profileId: string) =>
      dispatch('profile:get_profile:v1:requested', { profile_id: profileId }),
    setVendorProfile: (profileId: string, payload: Record<string, unknown>) =>
      dispatch('profile:set_vendor_profile:v1:requested', {
        profile_id: profileId,
        ...payload,
      }),
    setVendorStatus: (profileId: string, isOpen: boolean) =>
      dispatch('profile:set_vendor_status:v1:requested', {
        profile_id: profileId,
        is_open: isOpen,
      }),
    setVendorSchedule: (profileId: string, schedule: Record<string, unknown>) =>
      dispatch('profile:set_vendor_schedule:v1:requested', {
        profile_id: profileId,
        schedule,
      }),
    setVendorLocation: (profileId: string, payload: Record<string, unknown>) =>
      dispatch('profile:set_vendor_location:v1:requested', {
        profile_id: profileId,
        ...payload,
      }),
    listVendorLocations: (profileId: string) =>
      dispatch('profile:list_vendor_locations:v1:requested', { profile_id: profileId }),
    deleteVendorLocation: (profileId: string, locationId: string) =>
      dispatch('profile:delete_vendor_location:v1:requested', {
        profile_id: profileId,
        location_id: locationId,
      }),
  },
  assist: {
    createHelpTicket: (payload: Record<string, unknown>) =>
      dispatch('assist:create_help_ticket:v1:requested', payload),
  },
  /* Account closure. Deletion is SCHEDULED, not immediate: the server opens a
     30-day window (user.DeletionGracePeriod) during which the account keeps
     working and `cancelAccountDeletion` undoes it. The window length is read
     off the response (`grace_period_days`) rather than hardcoded here, so the
     number the app shows is always the one the server enforces. */
  account: {
    getDeletion: (profileId: string) =>
      dispatch('user:get_account_deletion:v1:requested', { profile_id: profileId }),
    requestDeletion: (profileId: string, confirmation: string, reason = '') =>
      dispatch('user:request_account_deletion:v1:requested', {
        profile_id: profileId,
        confirmation,
        reason,
      }),
    cancelDeletion: (profileId: string) =>
      dispatch('user:cancel_account_deletion:v1:requested', { profile_id: profileId }),
    /* Operator console. Support may list; only ops may change a status. */
    opsListUsers: (filter: { query?: string; role?: string; status?: string; cursor?: string } = {}) =>
      dispatch('user:ops_list_users:v1:requested', {
        query: filter.query ?? '',
        role: filter.role ?? '',
        status: filter.status ?? '',
        cursor: filter.cursor ?? '',
        page_size: 25,
      }),
    setUserStatus: (profileId: string, status: 'active' | 'suspended', reason = '') =>
      dispatch('user:set_user_status:v1:requested', { profile_id: profileId, status, reason }),
  },
  /* Chef verification (verification spec). Chef commands resolve the kitchen
     from the signed-in account, never from a payload id. Results come back
     flat: read them with commandPayload(). */
  verification: {
    getMine: () => dispatch('verification:get_my_verification:v1:requested', {}),
    requestPhoneOtp: (phoneNumber: string) =>
      dispatch('verification:request_phone_otp:v1:requested', { phone_number: phoneNumber }),
    confirmPhoneOtp: (code: string) =>
      dispatch('verification:confirm_phone_otp:v1:requested', { code }),
    acceptAgreement: (agreementVersion: string) =>
      dispatch('verification:accept_agreement:v1:requested', { agreement_version: agreementVersion }),
    submit: () => dispatch('verification:submit_verification:v1:requested', {}),
    submitPayoutAccount: (payload: { bank_code: string; account_number: string; account_name: string }) =>
      dispatch('verification:submit_payout_account:v1:requested', payload),
    getMyPayoutAccount: () => dispatch('verification:get_my_payout_account:v1:requested', {}),
    setSickPause: (paused: boolean) =>
      dispatch('verification:set_sick_pause:v1:requested', { is_sick_paused: paused }),
    reportHygieneIssue: (orderId: string, reason: string, detail: string) =>
      dispatch('verification:report_hygiene_issue:v1:requested', { order_id: orderId, reason, detail }),
    /* Reviewer commands: the server requires the ops or admin role. */
    listReviewQueue: () => dispatch('verification:list_review_queue:v1:requested', {}),
    getDossier: (vendorProfileId: string) =>
      dispatch('verification:get_dossier:v1:requested', { vendor_profile_id: vendorProfileId }),
    reviewSubmission: (vendorProfileId: string, payload: Record<string, unknown>) =>
      dispatch('verification:review_submission:v1:requested', { vendor_profile_id: vendorProfileId, ...payload }),
    reviewCertificate: (vendorProfileId: string, decision: 'approve' | 'reject', notes: string) =>
      dispatch('verification:review_certificate:v1:requested', { vendor_profile_id: vendorProfileId, decision, notes }),
    recordInspection: (vendorProfileId: string, payload: Record<string, unknown>) =>
      dispatch('verification:record_inspection:v1:requested', { vendor_profile_id: vendorProfileId, ...payload }),
    resolveFlag: (flagId: string, payload: Record<string, unknown>) =>
      dispatch('verification:resolve_hygiene_flag:v1:requested', { flag_id: flagId, ...payload }),
    reinstate: (vendorProfileId: string, payload: Record<string, unknown>) =>
      dispatch('verification:reinstate_kitchen:v1:requested', { vendor_profile_id: vendorProfileId, ...payload }),
    suspendKitchen: (vendorProfileId: string, reason: string) =>
      dispatch('verification:suspend_kitchen:v1:requested', { vendor_profile_id: vendorProfileId, reason }),
    deleteKitchen: (vendorProfileId: string, reason: string) =>
      dispatch('verification:delete_kitchen:v1:requested', { vendor_profile_id: vendorProfileId, reason }),
    /** Register an approved kitchen's bank account as a Paystack subaccount. Idempotent. */
    provisionPayoutAccount: (vendorProfileId: string) =>
      dispatch('verification:provision_payout_account:v1:requested', { vendor_profile_id: vendorProfileId }),
  },
  message: {
    opsListThreads: (cursor = '') => dispatch('message:ops_list_threads:v1:requested', { cursor, page_size: 25 }),
    opsListMessages: (threadId: string, cursor = '') => dispatch('message:ops_list_messages:v1:requested', { thread_id: threadId, cursor, page_size: 50 }),
    /* Idempotent per order (one thread per order, migration 000005): calling
       this again returns the thread that already exists. That makes it the way
       either side RESOLVES a conversation, not just starts one — neither the
       customer nor the chef has to wait for the thread projection to arrive
       before they can read or write in it. */
    startOrderThread: (payload: Record<string, unknown>) =>
      dispatch('message:start_order_thread:v1:requested', payload),
    sendOrderMessage: (threadId: string, payload: Record<string, unknown>) =>
      dispatch('message:send_order_message:v1:requested', {
        thread_id: threadId,
        ...payload,
      }),
    /* Read the transcript straight from the server. The projection still
       streams messages, but it is no longer the only way one arrives — a chef
       whose mirror is behind was simply never shown what the customer wrote. */
    listOrderMessages: (threadId: string) =>
      dispatch('message:list_order_messages:v1:requested', { thread_id: threadId }),
  },
  delivery: {
    transition: (deliveryId: string, payload: Record<string, unknown>) =>
      dispatch('delivery:transition:v1:requested', {
        delivery_id: deliveryId,
        ...payload,
      }),
  },
  offer: {
    accept: (offerId: string, riderId: string) =>
      dispatch('offer:accept:v1:requested', {
        offer_id: offerId,
        rider_id: riderId,
      }),
    decline: (offerId: string, riderId: string, reason = 'rider_declined') =>
      dispatch('offer:decline:v1:requested', {
        offer_id: offerId,
        rider_id: riderId,
        reason,
      }),
  },
  rider: {
    startDash: (riderId: string, latitude: number, longitude: number) =>
      dispatch('rider:start:v1:requested', {
        rider_id: riderId,
        latitude,
        longitude,
      }),
    stopDash: (riderId: string, dashSessionId: string) =>
      dispatch('rider:stop:v1:requested', {
        rider_id: riderId,
        dash_session_id: dashSessionId,
      }),
  },
  safety: {
    triggerSos: (payload: Record<string, unknown>) =>
      dispatch('safety:trigger_sos:v1:requested', payload),
  },
  support: {
    sendMessage: (payload: Record<string, unknown>) =>
      dispatch('support:send:v1:requested', payload),
  },
  proof: {
    confirmPickup: (payload: Record<string, unknown>) =>
      dispatch('proof:confirm_pickup_proof:v1:requested', payload),
    confirmDropoff: (payload: Record<string, unknown>) =>
      dispatch('proof:confirm_dropoff_proof:v1:requested', payload),
  },
  menu: {
    getKitchenBoard: (startDay: string) => dispatch('menu:get_kitchen_board:v1:requested', { start_day: startDay }),
    saveKitchenPlan: (payload: Record<string, unknown>, idempotencyKey: string) => dispatch('menu:save_kitchen_plan:v1:requested', payload, { idempotencyKey }),
    saveKitchenStock: (payload: Record<string, unknown>, idempotencyKey: string) => dispatch('menu:save_kitchen_stock:v1:requested', payload, { idempotencyKey }),
    listDishReviews: (dishId: string) => dispatch('menu:list_dish_reviews:v1:requested', { dish_id: dishId }),
    addDishReview: (dishId: string, payload: Record<string, unknown>) => dispatch('menu:add_dish_review:v1:requested', { ...payload, dish_id: dishId }),
    listCategories: () => dispatch('menu:list_categories:v1:requested', {}),
    catalogPolicy: () => dispatch('menu:get_catalog_policy:v1:requested', {}),
    manageCategory: (payload: Record<string, unknown>) => dispatch('menu:manage_category:v1:requested', payload),
    requestCategory: (payload: Record<string, unknown>) => dispatch('menu:request_category:v1:requested', payload),
    categoryRequests: () => dispatch('menu:category_requests:v1:requested', {}),
    declineCategory: (id: string, note: string) => dispatch('menu:decline_category:v1:requested', { request_id: id, review_note: note }),
    updateMenu: (menuId: string, payload: Record<string, unknown>, idempotencyKey?: string) =>
      dispatch('menu:update_menu:v1:requested', { ...payload, menu_id: menuId }, { idempotencyKey }),
    updateMenuCover: (menuId: string, coverImageUrl: string) =>
      dispatch('menu:update_menu_cover:v1:requested', { menu_id: menuId, cover_image_url: coverImageUrl }),
    createMenu: (payload: Record<string, unknown>, idempotencyKey?: string) =>
      dispatch('menu:create_menu:v1:requested', payload, { idempotencyKey }),
    publishMenu: (menuId: string, status: string) =>
      dispatch('menu:publish_menu:v1:requested', { menu_id: menuId, status }),
    addDish: (menuId: string, payload: Record<string, unknown>) =>
      dispatch('menu:add_dish:v1:requested', { menu_id: menuId, ...payload }),
    /** Standalone library dish — no menu attachment; chef_profile_id in payload. */
    createDish: (payload: Record<string, unknown>) =>
      dispatch('menu:create_dish:v1:requested', payload),
    getDish: (dishId: string) =>
      dispatch('menu:get_dish:v1:requested', { dish_id: dishId }),
    listChefDishes: (chefProfileId: string) =>
      dispatch('menu:list_chef_dishes:v1:requested', { chef_profile_id: chefProfileId }),
    setDishStatus: (dishId: string, status: string) =>
      dispatch('menu:set_dish_status:v1:requested', { dish_id: dishId, status }),
    /**
     * Patch a dish. Only the keys present in `patch` are written — send
     * `{ image_url: url }` to swap a photo and nothing else changes; send
     * `{ image_url: '' }` to clear it. Omitting a key leaves it alone.
     */
    updateDish: (dishId: string, patch: Record<string, unknown>) =>
      dispatch('menu:update_dish:v1:requested', { dish_id: dishId, ...patch }),
    deleteDish: (dishId: string) =>
      dispatch('menu:delete_dish:v1:requested', { dish_id: dishId }),
    deleteMenu: (menuId: string) =>
      dispatch('menu:delete_menu:v1:requested', { menu_id: menuId }),
    createMealPlan: (payload: Record<string, unknown>) =>
      dispatch('menu:create_meal_plan:v1:requested', payload),
    listMealPlans: (ownerProfileId: string) =>
      dispatch('menu:list_meal_plans:v1:requested', { owner_profile_id: ownerProfileId }),
    addMealPlanEntry: (planId: string, payload: Record<string, unknown>) =>
      dispatch('menu:add_meal_plan_entry:v1:requested', { plan_id: planId, ...payload }),
    removeMealPlanEntry: (planId: string, entryId: string) =>
      dispatch('menu:remove_meal_plan_entry:v1:requested', {
        plan_id: planId,
        entry_id: entryId,
      }),
    setMealPlanStatus: (planId: string, status: string) =>
      dispatch('menu:set_meal_plan_status:v1:requested', { plan_id: planId, status }),
    /**
     * Patch a meal plan (same key-presence semantics as `updateDish`). Serves
     * both a customer's personal plan and a chef's offering; the pricing/zone
     * keys are rejected on a personal plan, and ownership is enforced on the
     * row, so the caller does not branch on which kind it holds.
     */
    updateMealPlan: (planId: string, patch: Record<string, unknown>) =>
      dispatch('menu:update_meal_plan:v1:requested', { plan_id: planId, ...patch }),
    listMealPlanCycles: (planId: string) =>
      dispatch('menu:list_meal_plan_cycles:v1:requested', { plan_id: planId }),
    generateMealPlanCycles: (planId: string, startsOn: string, count = 4) =>
      dispatch('menu:generate_meal_plan_cycles:v1:requested', {
        plan_id: planId,
        starts_on: startsOn,
        count,
      }),
    setMealPlanCycleStatus: (
      cycleId: string,
      status: string,
      totalMinor?: number,
    ) =>
      dispatch('menu:set_meal_plan_cycle_status:v1:requested', {
        cycle_id: cycleId,
        status,
        // Omitted entirely when undefined so the server keeps the stored total.
        ...(totalMinor === undefined ? {} : { total_minor: totalMinor }),
      }),
    favouriteDish: (dishId: string, profileId: string) =>
      dispatch('menu:favourite_dish:v1:requested', {
        dish_id: dishId,
        profile_id: profileId,
      }),
    unfavouriteDish: (dishId: string, profileId: string) =>
      dispatch('menu:unfavourite_dish:v1:requested', {
        dish_id: dishId,
        profile_id: profileId,
      }),
    listFavouriteDishes: (profileId: string) =>
      dispatch('menu:list_favourite_dishes:v1:requested', { profile_id: profileId }),
    listChefMenus: (chefProfileId?: string, status?: string) => {
      const payload: Record<string, string> = {}
      if (chefProfileId) payload.chef_profile_id = chefProfileId
      if (status) payload.status = status
      return dispatch('menu:list_chef_menus:v1:requested', payload)
    },
    listDishes: (menuId: string) =>
      dispatch('menu:list_dishes:v1:requested', { menu_id: menuId }),
    listTrendingChefs: (windowDays = 7, limit = 8) =>
      dispatch('menu:list_trending_chefs:v1:requested', {
        window_days: windowDays,
        limit,
      }),
    listTrendingDishes: (windowDays = 7, limit = 8) =>
      dispatch('menu:list_trending_dishes:v1:requested', {
        window_days: windowDays,
        limit,
      }),
  },
} as const

/* ── image upload seam ───────────────────────────────────────────────── */

/**
 * Result of uploading an image. `url` is what the caller stores on the target
 * entity (chef avatar / menu cover / dish photo) — images are *optional but
 * preferred* across all three; this seam is the only path for either mode.
 */
export interface UploadedImage {
  url: string
  key: string
  contentType: string
  size: number
  /** True in offline/prototype mode — the URL is a browser object URL only. */
  local: boolean
  /** Soft advice about the original photo (dark, small, very tall…). Never blocks. */
  advisories: ImageAdvisory[]
}

/**
 * Say what went wrong with an upload in words a chef can act on. A bare
 * "upload failed: 413" tells nobody to pick a smaller file.
 */
export const uploadFailureMessage = (status: number, serverMessage = ''): string => {
  if (status === 413) return 'That file is too large. Choose a smaller photo or a shorter video.'
  if (status === 415 || status === 400) return serverMessage || 'That file type is not supported. Use a JPEG, PNG or WebP photo.'
  if (status === 401) return 'Your session has ended. Sign in again, then retry the upload.'
  if (status >= 500) return 'The upload did not finish on our side. Try again in a moment.'
  return serverMessage || `The upload did not finish (${status}). Try again.`
}

/** A dropped connection reads differently from a refused file. */
const uploadNetworkMessage = (error: unknown): Error =>
  new Error(error instanceof DOMException && (error.name === 'TimeoutError' || error.name === 'AbortError')
    ? 'The upload took too long. Check your connection and try again.'
    : 'The upload could not reach ChooseChow. Check your connection and try again.')

/** Owner scope informs the backend's storage key prefix. */
export type UploadScope = 'chef' | 'menu' | 'dish' | 'profile'

/**
 * Upload an image once and reuse the returned `url` on whatever entity the
 * creation flow lands on. In offline mode (`!isLiveRuntime()`) the file
 * becomes a `blob:` URL via `createObjectURL` — same shape as the live
 * response so the UI never branches on mode. In live mode the file is POSTed
 * as multipart to `/v1/media/uploads`; the backend uses `transfer.Manager` +
 * `objectstore` to stream the bytes and emit
 * `media:upload:v1:requested|success|failed` events.
 *
 * Note: multipart bypasses the command bus by design — `createAppRuntime` is
 * JSON-shaped for the dispatch lane. Headers (auth + edition + role) match
 * what `transportHeaders` injects everywhere else.
 */
export const uploadImage = async (
  rawFile: File,
  scope: UploadScope,
  ownerId: string,
): Promise<UploadedImage> => {
  // Ingest before anything else: downscale + re-encode within the scope's
  // budget so both the offline preview and the live upload carry the compressed
  // bytes (fast card paints, less storage). Throws only for over-ceiling inputs.
  const { file, advisories } = await prepareImage(rawFile, scope)

  if (!isLiveRuntime()) {
    return {
      url: URL.createObjectURL(file),
      key: `local/${scope}/${ownerId}/${Date.now()}-${file.name}`,
      contentType: file.type || 'image/jpeg',
      size: file.size,
      local: true,
      advisories,
    }
  }

  const form = new FormData()
  form.append('file', file)
  form.append('owner_scope', scope)
  form.append('owner_id', ownerId)
  if (file.type) form.append('content_type', file.type)

  const headers = transportHeaders()
  headers['X-Correlation-Id'] =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`

  let resp: Response
  try {
    resp = await fetch(`${API_BASE_URL}/v1/media/uploads`, {
      method: 'POST',
      headers,
      body: form,
      signal: AbortSignal.timeout(60_000),
    })
  } catch (error) {
    throw uploadNetworkMessage(error)
  }
  if (!resp.ok) {
    if (resp.status === 401 && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('chow:auth:unauthorized'))
    }
    const body = (await resp.json().catch(() => ({}))) as Record<string, unknown>
    throw new Error(uploadFailureMessage(resp.status, typeof body.message === 'string' ? body.message : ''))
  }
  const body = (await resp.json()) as {
    url: string
    key: string
    content_type: string
    size: number
  }
  return {
    url: body.url,
    key: body.key,
    contentType: body.content_type,
    size: body.size,
    local: false,
    advisories,
  }
}

/* ── private verification documents ─────────────────────────────────── */

export type VerificationDocumentKind =
  | 'id_document'
  | 'selfie'
  | 'kitchen_photo'
  | 'kitchen_video'
  | 'food_handler_cert'

export interface VerificationUploadFields {
  slot?: string
  idDocumentType?: 'nin_slip' | 'drivers_licence'
  certIssuer?: string
  certExpiry?: string
}

/**
 * Upload one verification document. These are identity documents and the
 * inside of people's homes, so they never go through the public media route:
 * `/v1/verification/documents` stores them under a private prefix and checks
 * the real file signature. Photos are NOT recompressed here — an ID must stay
 * legible for the reviewer. Returns the refreshed verification view.
 */
export const uploadVerificationDocument = async (
  file: File,
  kind: VerificationDocumentKind,
  fields: VerificationUploadFields = {},
): Promise<Record<string, unknown>> => {
  if (!isLiveRuntime()) {
    throw new Error('Verification uploads need a live connection.')
  }
  // Photos are resized only when large, and never below ID-legible size.
  if (kind !== 'kitchen_video') file = await prepareDocumentPhoto(file)
  const form = new FormData()
  form.append('file', file)
  form.append('kind', kind)
  if (fields.slot) form.append('slot', fields.slot)
  if (fields.idDocumentType) form.append('id_document_type', fields.idDocumentType)
  if (fields.certIssuer) form.append('cert_issuer', fields.certIssuer)
  if (fields.certExpiry) form.append('cert_expiry', fields.certExpiry)
  if (file.type) form.append('content_type', file.type)
  const headers = transportHeaders()
  // A one-minute kitchen video on mobile data is slow; a photo is not.
  const timeout = kind === 'kitchen_video' ? 10 * 60_000 : 2 * 60_000
  let resp: Response
  try {
    resp = await fetch(`${API_BASE_URL}/v1/verification/documents`, { method: 'POST', headers, body: form, signal: AbortSignal.timeout(timeout) })
  } catch (error) {
    throw uploadNetworkMessage(error)
  }
  const body = (await resp.json().catch(() => ({}))) as Record<string, unknown>
  if (!resp.ok) {
    if (resp.status === 401 && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('chow:auth:unauthorized'))
    }
    throw new Error(uploadFailureMessage(resp.status, typeof body.message === 'string' ? body.message : ''))
  }
  return body
}

/**
 * Fetch a private verification document as an object URL. An <img src> cannot
 * carry the bearer token, and the server sends no-store, so the bytes are read
 * with auth and shown from memory. Revoke the URL when the view unmounts.
 */
export const fetchVerificationDocument = async (key: string): Promise<string> => {
  const path = key.split('/').map(encodeURIComponent).join('/')
  const resp = await fetch(`${API_BASE_URL}/v1/verification/documents/${path}`, { headers: transportHeaders() })
  if (!resp.ok) throw new Error(`document unavailable (${resp.status})`)
  return URL.createObjectURL(await resp.blob())
}
